import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField, FormService } from '@shared/services/FormServices';
import { Button } from '@shared/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useGetResourceTypeList, useGetResourceSubtypeList } from '@features/mx/hooks';
import { useGetResources } from '@features/mx/resources/hook';
import { useResourceOperationChoices, useAddOperation } from '../../hooks';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { PageHeader } from '@shared/ui/pageHeader';
import { Summary, OperationSummaryData } from './Summary';

interface ResourceItem {
  id: string | number;
  resourceId: string;
  meterNo?: string;
  serialNo: string;
  utilityService: string;
  name?: string;
  type?: string;
  subType?: string;
  location?: string;
}

export const Create: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId() || '';

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: resourceOperations } = useResourceOperationChoices();
  const operationTypeOptions = resourceOperations?.result?.map((item: any) => ({
    value: item.key,
    label: item.value,
  }));

  // 1. Fetch Resource Types from shared/mx hooks
  const { data: resourceTypeResponse } = useGetResourceTypeList(remoteUtilityId);
  const resourceTypeOptions = useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    if (Array.isArray(results) && results.length > 0) {
      return results.map((item: any) => ({
        label: item.name || item.code || item,
        value: item.code || item.name || item,
      }));
    }
    return [];
  }, [resourceTypeResponse]);

  // 2. Fetch Sub Types from shared/mx hooks based on selected Resource Type
  // Note: temporary placeholder watch variables will be replaced by form watch below
  const [tempWatch, setTempWatch] = useState<{ resourceType?: string; subType?: string; resourceId?: string }>({});
  const watchResourceType = tempWatch.resourceType;
  const watchSubType = tempWatch.subType;
  const watchResourceId = tempWatch.resourceId;

  const { data: subtypesResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    watchResourceType ? [watchResourceType] : undefined,
  );
  const subTypeOptions = useMemo(() => {
    const results = subtypesResponse?.result || subtypesResponse || [];
    if (Array.isArray(results) && results.length > 0) {
      return results.map((item: any) => ({
        label: item.name || item.code || item,
        value: item.code || item.name || item,
      }));
    }
    return [];
  }, [subtypesResponse]);

  // 3. Fetch Resources from hook
  const { data: resourcesResponse, isFetching: isResourcesLoading } = useGetResources(
    remoteUtilityId,
    searchQuery,
    undefined,
    currentPage,
    pageSize,
    undefined,
    watchResourceType,
    watchSubType,
  );

  const availableResources = useMemo<ResourceItem[]>(() => {
    const apiResources = resourcesResponse?.results || [];
    if (Array.isArray(apiResources) && apiResources.length > 0) {
      return apiResources.map((item: any) => ({
        id: item.id,
        resourceId: item.meterNumber || item.meter_number || item.resourceId || item.resource_id || item.code || `RES-${item.id}`,
        meterNo: item.meterNumber || item.meter_number || item.meterNo || item.meter_no || item.code || item.resourceId || item.id,
        serialNo: item.deviceNo || item.device_no || item.serialNo || item.serial_number || item.sn || item.serialNumber || 'SN-0000',
        utilityService: item.productCode || item.product_code || item.utilityService || item.utility_service || item.service || 'Water',
        name: item.name || item.resourceName || `${item.resourceTypeDisplay || item.resourceType || 'Resource'} – ${item.warehouseName || item.warehouse_name || item.premise || item.location || 'Site'}`,
        type: item.resourceTypeCode || item.resource_type_code || item.resourceType || item.type || 'Meter',
        subType: item.resourceSubTypeCode || item.resource_sub_type_code || item.resourceSubType || item.subType || item.subtype || 'Manual Meter',
        location: item.warehouseName || item.warehouse_name || item.location || item.premise || item.area || 'Zone A',
      }));
    }
    return [];
  }, [resourcesResponse]);

  const paginationInfo = useMemo(() => {
    const totalItems = resourcesResponse?.count || 0;
    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: pageSize,
      startItem,
      endItem,
    };
  }, [resourcesResponse?.count, currentPage]);

  // Find currently selected resource object for summary display
  const selectedResourceObj = useMemo(() => {
    return availableResources.find((r) => r.resourceId === watchResourceId);
  }, [availableResources, watchResourceId]);

  // Table Columns for GenericTable
  const resourceColumns: TableColumn<ResourceItem>[] = useMemo(
    () => [
      {
        key: 'resourceId',
        header: 'Resource ID',
        sortable: true,
        render: (val, row) => (
          <span className="font-semibold text-blue-600">
            {row.resourceId}
          </span>
        ),
      },
      {
        key: 'serialNo',
        header: 'Serial No',
        sortable: true,
        render: (val, row) => (
          <span className="text-gray-700 font-mono text-xs">{row.serialNo}</span>
        ),
      },
      {
        key: 'utilityService',
        header: 'Utility Service',
        sortable: true,
        render: (val, row) => (
          <span className="text-gray-700 text-sm">{row.utilityService}</span>
        ),
      },
    ],
    [],
  );

  const fields: FormField[] = [
    {
      name: 'operationType',
      label: 'Operation Type',
      group: "Operation Details",
      groupBorderNeeded: false,
      groupColorNeeded: false,
      type: 'select',
      required: true,
      validation: { required: 'Operation type is required' },
      placeholder: 'Select operation type...',
      classes: FIELD_CLASSES,
      options: operationTypeOptions,
    },
    {
      name: 'scheduledDate',
      label: 'Scheduled Date',
      type: 'datepicker',
      group: "Operation Details",
      required: true,
      validation: { required: 'Scheduled date is required' },
      classes: FIELD_CLASSES,
      placeholder: 'dd-mm-yyyy',
    },
    {
      name: 'resourceType',
      label: 'Resource Type',
      type: 'select',
      group: "Operation Details",
      required: true,
      validation: { required: 'Resource type is required' },
      placeholder: 'Select resource type...',
      classes: FIELD_CLASSES,
      options: resourceTypeOptions,
    },
    {
      name: 'subType',
      label: 'Sub Type',
      type: 'select',
      group: "Operation Details",
      required: true,
      validation: { required: 'Sub type is required' },
      placeholder: 'Select sub type...',
      classes: FIELD_CLASSES,
      options: subTypeOptions,
      disabled: (values) => !values.resourceType,
    },
    {
      name: 'resourceId',
      label: 'Select Resource',
      group: "Operation Details",
      type: 'custom',
      required: true,
      validation: { required: 'Resource selection is required' },
      className: 'col-span-2',
      showWhen: (values) => !!values.subType,
      customRender: (_field, customForm, error) => {
        const currentSelectedId = customForm.watch('resourceId');
        return (
          <div className="space-y-2 pt-1">
            <div className="overflow-hidden">
              <GenericTable<ResourceItem>
                data={availableResources}
                columns={resourceColumns}
                rowKey="resourceId"
                selectable={true}
                hideSelectAll={true}
                selectedRows={currentSelectedId ? [currentSelectedId] : []}
                onSelectionChange={(selectedKeys) => {
                  const lastSelected = selectedKeys[selectedKeys.length - 1];
                  customForm.setValue('resourceId', lastSelected || '', { shouldValidate: true, shouldTouch: true });
                }}
                searchPlaceholder="Search by ID, name, location, serial no..."
                searchValue={searchQuery}
                onSearchValueChange={(val) => { setSearchQuery(val); setCurrentPage(1); }}
                onSearch={(val) => { setSearchQuery(val); setCurrentPage(1); }}
                emptyMessage="No resources found matching the criteria."
                pagination={paginationInfo}
                onPageChange={(page) => setCurrentPage(page)}
                loading={isResourcesLoading}
              />
            </div>
            {error && <p className="text-red-500 text-xs font-medium mt-1">{error}</p>}
          </div>
        );
      },
    },
    {
      name: 'notes',
      label: 'Remark',
      group: "Operation Details",
      type: 'textarea',
      classes: FIELD_CLASSES,
      className: 'col-span-2',
      placeholder: 'Add field instructions or notes...',
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: 'onChange',
    defaultValues: {
      operationType: '',
      scheduledDate: '',
      resourceType: '',
      subType: '',
      resourceId: '',
      notes: '',
    },
  });

  const watchValues = useWatch({ control: form.control });

  const prevResourceTypeRef = React.useRef(watchValues?.resourceType);
  const prevSubTypeRef = React.useRef(watchValues?.subType);

  React.useEffect(() => {
    if (watchValues?.resourceType !== prevResourceTypeRef.current) {
      if (prevResourceTypeRef.current !== undefined) {
        form.setValue('subType', '', { shouldValidate: true, shouldTouch: true });
        form.setValue('resourceId', '', { shouldValidate: true, shouldTouch: true });
      }
      prevResourceTypeRef.current = watchValues?.resourceType;
    }

    if (watchValues?.subType !== prevSubTypeRef.current) {
      if (prevSubTypeRef.current !== undefined) {
        form.setValue('resourceId', '', { shouldValidate: true, shouldTouch: true });
      }
      prevSubTypeRef.current = watchValues?.subType;
    }

    if (watchValues) {
      setTempWatch(watchValues);
    }
  }, [watchValues?.resourceType, watchValues?.subType, watchValues?.resourceId]);

  // Summary object for right sidebar
  const summaryData: OperationSummaryData = {
    opType: operationTypeOptions?.find((opt: any) => String(opt.value) === String(watchValues?.operationType))?.label || watchValues?.operationType || '—',
    date: watchValues?.scheduledDate || '—',
    resType: resourceTypeOptions.find((opt: any) => opt.value === watchValues?.resourceType)?.label || watchValues?.resourceType || '—',
    subType: watchValues?.subType ? (subTypeOptions.find((opt: any) => opt.value === watchValues?.subType)?.label || watchValues?.subType) : '—',
    resource: watchValues?.resourceId ? (selectedResourceObj?.meterNo || selectedResourceObj?.resourceId || watchValues?.resourceId) : '—',
  };

  const { mutate: createOperation, isPending: isCreating } = useAddOperation();

  const onSubmit = (data: any) => {
    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      operation_type: Number(data.operationType),
      scheduled_date: data.scheduledDate,
      resource: Number(selectedResourceObj?.id),
      notes: data.notes,
    };

    createOperation(payload, {
      onSuccess: () => {
        navigate('/mx/operations');
      }
    });
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Header */}
      <PageHeader
        title="Add New Operation"
        description="Select a resource and schedule the operation. Further steps are completed in the detail view."
        onBack={() => navigate('/mx/operations')}
      />

      {/* Main Grid: Form + Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Column */}
        <div className="lg:col-span-9 bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
          <DynamicForm
            form={form}
            fields={fields}
            onSubmit={onSubmit}
            gridColumns={2}
            config={{ showSubmitButton: false }}
            renderCustomFooter={() => <></>}
          />

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-5 border-t border-gray-100">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/mx/operations')}
              className="text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isCreating}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5 px-5"
            >
              <span>{isCreating ? 'Adding...' : 'Add Operation'}</span>
              {!isCreating && <ArrowRight className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Right Summary Column */}
        <div className="lg:col-span-3 sticky top-6">
          <Summary summary={summaryData} />
        </div>
      </div>
    </div>
  );
};
